export type { DeliverySummaryDto as DeliverySummary } from '../generated/delivery';

import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { API_CONFIG } from '../config/env';
import {
  DeliveryControllerApi,
  DeliverySummaryDto as DeliverySummary,
  DeliveryCreateDto as DeliveryRequest,
  FindAllDeliveryTypesEnum,
  FindAllStatusesEnum,
} from '../generated/delivery';
import { QK_DELIVERY_PENDING_SHIPMENTS, QK_DELIVERY_REPLACEABLE } from '../query-keys';

const QX_DELIVERY_URL = API_CONFIG.QX_DELIVERY_URL;
const deliveryControllerApi = new DeliveryControllerApi(undefined, QX_DELIVERY_URL);

export const useCreateDelivery = () => {
  return useMutation<DeliverySummary, AxiosError, DeliveryRequest>({
    mutationFn: (body) => deliveryControllerApi.createDelivery(body).then((res) => res.data),
  });
};

/** 배송 요청 링크 재발송 — 서버가 처방 id 기준에서 배송 id 기준으로 바뀌었다. */
export const useResendLink = () => {
  return useMutation<void, AxiosError, { deliveryId: string }>({
    mutationFn: ({ deliveryId }) => deliveryControllerApi.resendRequestMessage(deliveryId).then((res) => res.data),
  });
};

/**
 * 배송 요청 전(PENDING) 일반 배송건 — 병원 토큰이면 자기 병원 것만 조회된다.
 * 처방에는 deliveryId 가 없어서, 알림톡 재발송 대상을 prescriptionId 로 찾으려면 이 목록이 필요하다.
 * 재발송 API 가 허용하는 조건(PENDING + 일반 배송)과 같은 필터라 결과가 곧 재발송 가능한 건이다.
 */
export const useFetchPendingShipments = () => {
  return useQuery<DeliverySummary[], AxiosError>({
    queryKey: [QK_DELIVERY_PENDING_SHIPMENTS],
    queryFn: () =>
      deliveryControllerApi
        .findAll(undefined, undefined, undefined, [FindAllStatusesEnum.Pending], undefined, undefined, undefined, undefined, undefined, [
          FindAllDeliveryTypesEnum.Shipment,
        ])
        .then((res) => res.data),
    // 처방 생성 직후 대시보드로 오면 새 배송건이 필요하다 — 전역 기본 캐시(3시간)를 끈다.
    gcTime: 0,
    staleTime: 0,
  });
};

// deliveryType: Shipment | ExchangeShipment, status: Delivered 인 배송 조회
export const useFindReplaceableDelivery = (prescriptionId?: string) => {
  return useQuery<DeliverySummary | null, AxiosError>({
    queryKey: [QK_DELIVERY_REPLACEABLE, prescriptionId],
    queryFn: () =>
      deliveryControllerApi
        .findReplaceable(prescriptionId!)
        .then((res) => res.data)
        .catch(() => null),
    enabled: !!prescriptionId,
  });
};
